<script>
    // SECONDARY PAGE: Users reach this page via the "older than 30 days" link on ImportFromJobTread.
    // The primary import page is ImportFromJobTread.svelte. Keep feature parity for shared concerns.

    import { onMount } from 'svelte';
    import Layout from '../components/Layout.svelte';

    let jtInvoices = [];
    let invoicesToSync = [];
    let isLoading = false;
    let error = null;
    let selectedInvoices = [];
    let selectAll = false;
    let nextPage = null;
    let isLoadingMore = false;
    
    // Modal state for mark as synced confirmation
    let showMarkSyncedModal = false;
    let pendingMarkSyncedIds = [];

    // Filter variables (matching PO pattern)
    let jobsFromInvoices = []; // Jobs extracted from invoice data
    let selectedFilter = "30";  // Default to Last 30 days
    let selectedJob = null;
    let showContent = false;
    let hideSynced = true; // Default: hide already synced invoices
    let prevHideSynced = hideSynced;
    let showPaid = false; // Default: only show pending invoices; when true, include paid invoices
    let showDrafts = false;
    let allInvoicesForJobFilter = []; // Store all invoices when using "By Job" filter
    let searchQuery = ""; // Search filter

    // JobTread has no "paid" status; paid = amountPaid >= total
    function isPaid(inv) {
        const total = inv.total || inv.priceWithTax || 0;
        const amountPaid = inv.amountPaid || 0;
        return total > 0 && amountPaid >= total;
    }

    function isDraft(inv) {
        return inv.status === 'draft';
    }

    // Filter invoices by search query
    function getFilteredInvoices(invoices, search) {
        if (!search || search.trim().length < 2) {
            return invoices;
        }
        const query = search.toLowerCase();
        return invoices.filter(inv => 
            (inv.customer && inv.customer.toLowerCase().includes(query)) ||
            (inv.jobName && inv.jobName.toLowerCase().includes(query)) ||
            (inv.jobNumber && String(inv.jobNumber).toLowerCase().includes(query)) ||
            (inv.number && String(inv.number).toLowerCase().includes(query)) ||
            (inv.subject && inv.subject.toLowerCase().includes(query))
        );
    }

    function handleSearch(event) {
        searchQuery = event.target.value;
    }

    // Reactive: apply search filter to invoicesToSync
    $: displayedInvoices = getFilteredInvoices(invoicesToSync, searchQuery);

    onMount(async () => {
        console.log('Component mounted');
        // Auto-load with default filter (Last 30 days)
        showContent = true;
        await fetchAllInvoices();
    });

    // Extract unique jobs from invoice data
    function extractJobsFromInvoices(invoices) {
        const jobMap = new Map();
        invoices.forEach(inv => {
            if (inv.jobId && inv.jobName) {
                jobMap.set(inv.jobId, {
                    id: inv.jobId,
                    name: inv.jobName,
                    number: inv.jobNumber,
                    customer: inv.customer
                });
            }
        });
        // Sort by job name
        return Array.from(jobMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }

    async function handleFilterChange(event) {
        selectedFilter = event.target.value;
        if (selectedFilter !== 'job' && selectedFilter !== 'job-all') {
            selectedJob = null;
            jobsFromInvoices = [];
            allInvoicesForJobFilter = [];
        }
        if (selectedFilter) {
            showContent = true;
            if (selectedFilter === 'job') {
                // When "By Job (90 days)" is selected, fetch 90 days of invoices to populate job dropdown
                await fetchInvoicesForJobFilter(90);
            } else if (selectedFilter === 'job-all') {
                // When "By Job (all time)" is selected, fetch all invoices to populate job dropdown
                await fetchInvoicesForJobFilter(null);
            } else {
                await fetchAllInvoices();
            }
        }
    }

    async function handleJobChange(event) {
        selectedJob = event.target.value;
        if (selectedJob) {
            // Query from scratch for this job so pagination (Load more) is accurate
            await fetchAllInvoices();
        } else {
            jtInvoices = [];
            invoicesToSync = [];
        }
    }

    async function handleShowPaidChange() {
        // Refresh current filter view with/without paid invoices
        selectedInvoices = [];
        selectAll = false;
        if (selectedFilter) {
            showContent = true;
            if (selectedFilter === 'job' || selectedFilter === 'job-all') {
                await fetchInvoicesForJobFilter(selectedFilter === 'job' ? 90 : null);
                if (selectedJob) {
                    filterInvoicesByJob(selectedJob);
                }
            } else {
                await fetchAllInvoices();
            }
        }
    }

    async function handleShowDraftsChange() {
        selectedInvoices = [];
        selectAll = false;
        if (selectedFilter) {
            showContent = true;
            if (selectedFilter === 'job' || selectedFilter === 'job-all') {
                await fetchInvoicesForJobFilter(selectedFilter === 'job' ? 90 : null);
                if (selectedJob) {
                    filterInvoicesByJob(selectedJob);
                }
            } else {
                await fetchAllInvoices();
            }
        }
    }

    // Fetch invoices for "By Job" filter and extract unique jobs
    // days = number for limited time, null for all time
    // Auto-paginates through ALL pages so the job dropdown is fully populated.
    async function fetchInvoicesForJobFilter(days) {
        isLoading = true;
        error = null;
        jtInvoices = [];
        invoicesToSync = [];
        nextPage = null;
        selectedJob = null;

        try {
            const jobFilterParams = new URLSearchParams();
            if (days) jobFilterParams.set('days', String(days));
            if (showDrafts) jobFilterParams.set('status', 'draft,pending,approved');

            let accumulated = [];
            let pageToken = null;
            let hasMore = true;

            while (hasMore) {
                const params = new URLSearchParams(jobFilterParams);
                if (pageToken) params.set('page', pageToken);
                let url = `/api/jt/invoices-all?${params.toString()}`;
                let jtResponse = await fetch(url);

                if (!jtResponse.ok && jtResponse.status === 401) {
                    const refreshResponse = await fetch('/api/refresh-jobtread-session', { method: 'POST' });
                    if (refreshResponse.ok) {
                        jtResponse = await fetch(url);
                        if (!jtResponse.ok) throw new Error('Failed to fetch JobTread invoices after session refresh');
                    } else {
                        throw new Error('Failed to refresh JobTread session. Please log out and log back in.');
                    }
                } else if (!jtResponse.ok) {
                    throw new Error('Failed to fetch JobTread invoices');
                }

                const jtData = await jtResponse.json();
                const pageInvoices = jtData.invoices || [];
                accumulated = [...accumulated, ...pageInvoices];
                console.log(`📄 [JOB FILTER] Fetched page (${pageInvoices.length} invoices, ${accumulated.length} total so far)`);

                if (jtData.nextPage && pageInvoices.length > 0) {
                    pageToken = jtData.nextPage;
                } else {
                    hasMore = false;
                }
            }

            allInvoicesForJobFilter = accumulated;
            
            // Extract unique jobs - when showPaid off, only from unpaid invoices
            const invoicesForJobs = showPaid ? allInvoicesForJobFilter : allInvoicesForJobFilter.filter(inv => !isPaid(inv));
            jobsFromInvoices = extractJobsFromInvoices(invoicesForJobs);
            console.log(`✅ Found ${jobsFromInvoices.length} unique jobs from ${allInvoicesForJobFilter.length} invoices`);

            // Check sync status for all invoices
            if (allInvoicesForJobFilter.length > 0) {
                const invoicesForCheck = allInvoicesForJobFilter.map(inv => ({
                    id: inv.id,
                    jobNumber: inv.jobNumber,
                    number: inv.number
                }));
                
                const syncResponse = await fetch('/api/jt/invoices/check-sync-status', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ invoices: invoicesForCheck })
                });
                
                if (syncResponse.ok) {
                    const syncData = await syncResponse.json();
                    const syncStatuses = syncData.syncStatuses || {};
                    
                    allInvoicesForJobFilter = allInvoicesForJobFilter.map(invoice => ({
                        ...invoice,
                        status: invoice.status,
                        isSynced: syncStatuses[invoice.id]?.isSynced || false,
                        syncSource: syncStatuses[invoice.id]?.source,
                        xeroInvoiceId: syncStatuses[invoice.id]?.xeroInvoiceId,
                        xeroInvoiceNumber: syncStatuses[invoice.id]?.xeroInvoiceNumber,
                        needsSync: invoice.status !== 'draft' && !syncStatuses[invoice.id]?.isSynced
                    }));
                }
            }

            // Don't show invoices yet - wait for job selection
            jtInvoices = [];
            invoicesToSync = [];
            
        } catch (err) {
            error = err.message;
            console.error('Error fetching invoices for job filter:', err);
        } finally {
            isLoading = false;
        }
    }

    // Filter invoices by selected job
    function filterInvoicesByJob(jobId) {
        jtInvoices = allInvoicesForJobFilter.filter(inv => inv.jobId === jobId);
        let base = hideSynced ? jtInvoices.filter(inv => !inv.isSynced) : jtInvoices;
        invoicesToSync = showPaid ? base : base.filter(inv => !isPaid(inv));
        
        console.log(`📊 Filtered to ${jtInvoices.length} invoices for job ${jobId}`);
        
        if (selectAll) {
            selectedInvoices = invoicesToSync.filter(inv => inv.needsSync).map(inv => inv.id);
        }
    }

    async function fetchMoreInvoices() {
        if (!nextPage || isLoadingMore) return;
        
        isLoadingMore = true;
        try {
            // Construct URL with filter parameters and page
            let url = '/api/jt/invoices-all';
            const params = new URLSearchParams();

            if ((selectedFilter === 'job' || selectedFilter === 'job-all') && selectedJob) {
                params.append('jobId', selectedJob);
                if (selectedFilter === 'job') params.append('days', '90');
            } else if (selectedFilter !== 'job' && selectedFilter !== 'job-all') {
                params.append('days', selectedFilter);
            }
            if (showDrafts) params.append('status', 'draft,pending,approved');
            params.append('page', nextPage);
            url = `${url}?${params.toString()}`;
            
            console.log('🔄 [LOAD MORE] Making request with pagination token:', nextPage);
            
            let jtResponse = await fetch(url);
            
            // Check if request failed with 401 and try to refresh session
            if (!jtResponse.ok && jtResponse.status === 401) {
                console.log('🔄 [REFRESH] Load more request failed with 401, attempting to refresh session...');
                
                const refreshResponse = await fetch('/api/refresh-jobtread-session', {
                    method: 'POST'
                });
                
                if (refreshResponse.ok) {
                    console.log('✅ [REFRESH] Session refreshed successfully, retrying load more request...');
                    
                    // Retry the request
                    jtResponse = await fetch(url);
                    if (!jtResponse.ok) {
                        throw new Error('Failed to fetch additional JobTread invoices after session refresh');
                    }
                } else {
                    throw new Error('Failed to refresh JobTread session. Please log out and log back in.');
                }
            } else if (!jtResponse.ok) {
                throw new Error('Failed to fetch additional JobTread invoices');
            }
            
            const jtData = await jtResponse.json();
            console.log('📦 [LOAD MORE] Server returned', jtData.invoices.length, 'invoices');

            const previousToken = nextPage;
            nextPage = jtData.nextPage;
            console.log('🎯 [LOAD MORE] Token update:', {
                previous: previousToken,
                new: nextPage,
                changed: previousToken !== nextPage
            });

            if (jtData.invoices && jtData.invoices.length > 0) {
                const existingInvoiceIds = new Set(jtInvoices.map(inv => inv.id));
                
                const newInvoices = jtData.invoices.filter(inv => {
                    const isDuplicate = existingInvoiceIds.has(inv.id);
                    if (isDuplicate) {
                        console.log('🔄 [LOAD MORE] Duplicate invoice found:', {
                            id: inv.id,
                            number: inv.number
                        });
                    }
                    return !isDuplicate;
                });

                if (newInvoices.length > 0) {
                    // Check sync status for new invoices
                    const invoicesForCheck = newInvoices.map(inv => ({
                        id: inv.id,
                        jobNumber: inv.jobNumber,
                        number: inv.number
                    }));
                    
                    const syncResponse = await fetch('/api/jt/invoices/check-sync-status', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ invoices: invoicesForCheck })
                    });
                    
                    if (syncResponse.ok) {
                        const syncData = await syncResponse.json();
                        const syncStatuses = syncData.syncStatuses || {};
                        
                        // Mark new invoices with sync status
                        const newInvoicesWithStatus = newInvoices.map(invoice => ({
                            ...invoice,
                            status: invoice.status,
                            isSynced: syncStatuses[invoice.id]?.isSynced || false,
                            syncSource: syncStatuses[invoice.id]?.source,
                            xeroInvoiceId: syncStatuses[invoice.id]?.xeroInvoiceId,
                            xeroInvoiceNumber: syncStatuses[invoice.id]?.xeroInvoiceNumber,
                            needsSync: invoice.status !== 'draft' && !syncStatuses[invoice.id]?.isSynced
                        }));
                        
                        jtInvoices = [...jtInvoices, ...newInvoicesWithStatus];
                        invoicesToSync = hideSynced 
                            ? jtInvoices.filter(inv => !inv.isSynced)
                            : jtInvoices;
                        
                        console.log(`📊 [LOAD MORE] Added ${newInvoicesWithStatus.length} invoices, ${newInvoicesWithStatus.filter(inv => !inv.isSynced).length} need sync`);
                    } else {
                        console.error('Failed to check sync status for new invoices');
                        const newInvoicesWithStatus = newInvoices.map(inv => ({ ...inv, status: inv.status, needsSync: inv.status !== 'draft' }));
                        jtInvoices = [...jtInvoices, ...newInvoicesWithStatus];
                        invoicesToSync = hideSynced 
                            ? jtInvoices.filter(inv => !inv.isSynced)
                            : jtInvoices;
                    }
                }
            }
            
            if (!jtData.nextPage || jtData.invoices.length === 0) {
                console.log('🏁 [LOAD MORE] No more pages available, disabling load more');
                nextPage = null;
            }
            
        } catch (err) {
            error = err.message;
            console.error('❌ [LOAD MORE] Error:', err);
        } finally {
            isLoadingMore = false;
        }
    }

    async function fetchAllInvoices() {
        if (!selectedFilter) return;

        isLoading = true;
        error = null;
        jtInvoices = [];
        invoicesToSync = [];
        nextPage = null;

        try {
            // Construct URL with filter parameters
            let url = '/api/jt/invoices-all';
            const params = new URLSearchParams();

            if ((selectedFilter === 'job' || selectedFilter === 'job-all') && selectedJob) {
                params.append('jobId', selectedJob);
                if (selectedFilter === 'job') params.append('days', '90');
            } else if (selectedFilter !== 'job' && selectedFilter !== 'job-all') {
                params.append('days', selectedFilter);
            }
            if (showDrafts) params.append('status', 'draft,pending,approved');
            url = `${url}?${params.toString()}`;
            
            // Fetch JobTread invoices
            let jtResponse = await fetch(url);

            // Check if JobTread request failed with 401 and try to refresh session
            if (!jtResponse.ok && jtResponse.status === 401) {
                console.log('🔄 [REFRESH] JobTread request failed with 401, attempting to refresh session...');
                
                const refreshResponse = await fetch('/api/refresh-jobtread-session', {
                    method: 'POST'
                });
                
                if (refreshResponse.ok) {
                    console.log('✅ [REFRESH] Session refreshed successfully, retrying request...');
                    
                    // Retry the JobTread request
                    jtResponse = await fetch(url);
                    if (!jtResponse.ok) {
                        throw new Error('Failed to fetch JobTread invoices after session refresh');
                    }
                } else {
                    throw new Error('Failed to refresh JobTread session. Please log out and log back in.');
                }
            } else if (!jtResponse.ok) {
                throw new Error('Failed to fetch JobTread invoices');
            }

            const jtData = await jtResponse.json();
            jtInvoices = jtData.invoices || [];
            nextPage = jtData.nextPage;
            
            console.log(`✅ Loaded ${jtInvoices.length} JobTread invoices`);

            // Check sync status via DynamoDB (with Xero fallback)
            if (jtInvoices.length > 0) {
                // Send full invoice objects so backend can build expected Xero invoice numbers
                const invoicesForCheck = jtInvoices.map(inv => ({
                    id: inv.id,
                    jobNumber: inv.jobNumber,
                    number: inv.number
                }));
                
                const syncResponse = await fetch('/api/jt/invoices/check-sync-status', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ invoices: invoicesForCheck })
                });
                
                if (syncResponse.ok) {
                    const syncData = await syncResponse.json();
                    const syncStatuses = syncData.syncStatuses || {};
                    
                    console.log('📋 Received sync statuses:', syncStatuses);
                    
                    // Mark invoices as synced/not synced
                    jtInvoices = jtInvoices.map(invoice => {
                        const syncStatus = syncStatuses[invoice.id];
                        console.log(`Invoice ${invoice.id} (${invoice.jobNumber}-${invoice.number}):`, syncStatus);
                        return {
                            ...invoice,
                            status: invoice.status,
                            isSynced: syncStatus?.isSynced || false,
                            syncSource: syncStatus?.source,
                            xeroInvoiceId: syncStatus?.xeroInvoiceId,
                            xeroInvoiceNumber: syncStatus?.xeroInvoiceNumber,
                            needsSync: invoice.status !== 'draft' && !syncStatus?.isSynced
                        };
                    });
                    
                    // Filter based on hideSynced toggle
                    invoicesToSync = hideSynced 
                        ? jtInvoices.filter(inv => !inv.isSynced)
                        : jtInvoices; // Show all if hideSynced is false
                    
                    console.log(`📊 Sync status: ${jtInvoices.filter(inv => inv.isSynced).length} synced, ${jtInvoices.filter(inv => !inv.isSynced).length} to sync`);
                    console.log('Synced invoices:', jtInvoices.filter(inv => inv.isSynced).map(inv => `${inv.jobNumber}-${inv.number}`));
                    console.log('Need sync:', jtInvoices.filter(inv => !inv.isSynced).map(inv => `${inv.jobNumber}-${inv.number}`));
                    
                    if (selectAll) {
                        selectedInvoices = invoicesToSync.filter(inv => inv.needsSync).map(inv => inv.id);
                    }
                } else {
                    console.error('Failed to check sync status, assuming all need sync');
                    invoicesToSync = jtInvoices.map(inv => ({ ...inv, status: inv.status, needsSync: inv.status !== 'draft' }));
                }
            }

        } catch (err) {
            error = err.message;
            console.error('Error fetching invoices:', err);
        } finally {
            isLoading = false;
        }
    }

    // OLD FUNCTION - No longer needed, using DynamoDB + Xero fallback instead
    // function computeInvoicesToSync() {
    //     console.log('Computing invoices to sync...');
    //     console.log('Xero invoice numbers:', xeroInvoices);
    //     
    //     // Create a Set of Xero invoice numbers for faster lookup
    //     const xeroInvoiceSet = new Set(xeroInvoices);
    //     
    //     invoicesToSync = jtInvoices.map(jtInvoice => {
    //         const fullInvoiceNumber = `${jtInvoice.jobNumber}-${jtInvoice.number}`;
    //         console.log('Checking invoice:', fullInvoiceNumber, 'exists in Xero:', xeroInvoiceSet.has(fullInvoiceNumber));
    //         return {
    //             ...jtInvoice,
    //             needsSync: !xeroInvoiceSet.has(fullInvoiceNumber)
    //         };
    //     });
    //
    //     if (selectAll) {
    //         selectedInvoices = invoicesToSync.filter(inv => inv.needsSync).map(inv => inv.id);
    //     }
    // }

    function handleSelectAll(event) {
        selectAll = event.target.checked;
        selectedInvoices = selectAll ? invoicesToSync.filter(inv => inv.needsSync).map(inv => inv.id) : [];
    }

    function handleInvoiceSelect(invoice, event) {
        if (!invoice.needsSync) return;

        if (event.target.checked) {
            selectedInvoices = [...selectedInvoices, invoice.id];
        } else {
            selectedInvoices = selectedInvoices.filter(id => id !== invoice.id);
            selectAll = false;
        }
    }

    function handleRowClick(invoice) {
        if (!invoice.needsSync || isDraft(invoice)) return;
        
        if (selectedInvoices.includes(invoice.id)) {
            selectedInvoices = selectedInvoices.filter(id => id !== invoice.id);
            selectAll = false;
        } else {
            selectedInvoices = [...selectedInvoices, invoice.id];
        }
    }

    // Reactive: refilter list when jtInvoices, hideSynced, showPaid, or showDrafts changes; clear selection when hideSynced toggles
    $: {
        if (jtInvoices.length > 0) {
            let base = hideSynced 
                ? jtInvoices.filter(inv => !inv.isSynced)
                : jtInvoices;
            if (!showPaid) base = base.filter(inv => !isPaid(inv));
            if (!showDrafts) base = base.filter(inv => !isDraft(inv));
            invoicesToSync = base;
            if (prevHideSynced !== hideSynced) {
                selectedInvoices = [];
                selectAll = false;
                prevHideSynced = hideSynced;
            }
        }
    }

    $: canMarkSelected = selectedInvoices.length > 0;

    function markSelectedAsSynced() {
        if (selectedInvoices.length === 0) {
            console.log('No invoices selected for manual sync marking');
            return;
        }

        // Show confirmation modal
        pendingMarkSyncedIds = [...selectedInvoices];
        showMarkSyncedModal = true;
    }

    async function confirmMarkAsSynced() {
        showMarkSyncedModal = false;
        
        if (pendingMarkSyncedIds.length === 0) {
            return;
        }

        try {
            console.log(`Marking ${pendingMarkSyncedIds.length} invoices as synced...`);
            
            const response = await fetch('/api/jt/mark-invoices-as-synced', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ invoiceIds: pendingMarkSyncedIds })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                console.log(`✅ Successfully marked ${result.successCount} invoices as synced`);
                
                // Update jtInvoices to mark selected invoices as synced
                jtInvoices = jtInvoices.map(invoice => {
                    if (pendingMarkSyncedIds.includes(invoice.id)) {
                        return {
                            ...invoice,
                            isSynced: true,
                            needsSync: false
                        };
                    }
                    return invoice;
                });
                
                // Clear selection
                selectedInvoices = [];
                selectAll = false;
                
                // Reactive statement will automatically update invoicesToSync based on hideSynced toggle
            } else {
                console.error('❌ Failed to mark invoices as synced:', result.error);
                alert(`Failed to mark invoices as synced: ${result.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('❌ Error marking invoices as synced:', error);
            alert(`Error marking invoices as synced: ${error.message}`);
        } finally {
            pendingMarkSyncedIds = [];
        }
    }

    function cancelMarkAsSynced() {
        showMarkSyncedModal = false;
        pendingMarkSyncedIds = [];
    }

    function handleModalBackdropClick(e) {
        if (e.target === e.currentTarget) {
            cancelMarkAsSynced();
        }
    }

    async function handleSyncAllClick() {
        const allDisplayedNeedingSync = displayedInvoices.filter(inv => inv.needsSync).map(inv => inv.id);
        if (allDisplayedNeedingSync.length === 0) {
            alert('No invoices in view need syncing.');
            return;
        }
        selectedInvoices = allDisplayedNeedingSync;
        selectAll = true;
        await handleSyncClick();
    }

    async function handleSyncClick() {
        console.log('Selected invoices:', invoicesToSync.filter(inv => selectedInvoices.includes(inv.id)));
        const selectedInvoicesData = invoicesToSync
            .filter(inv => selectedInvoices.includes(inv.id))
            .map(inv => {
                console.log('Processing invoice:', inv);
                return {
                    id: inv.id,
                    status: inv.status,
                    amountPaid: inv.amountPaid,
                    number: inv.number,
                    customer: inv.customer || inv.account?.name,  // Ensure we get the customer name
                    location: inv.location || inv.jobLocationAddress,  // Ensure we get the location
                    subject: inv.subject,
                    issueDate: inv.issueDate,
                    createdAt: inv.createdAt,
                    dueDate: inv.dueDate,
                    jobId: inv.jobId,
                    jobName: inv.jobName || inv.job?.name,
                    jobNumber: inv.jobNumber || inv.job?.number,
                    total: inv.total || inv.priceWithTax,
                    lineItems: (inv.lineItems || []).map(item => ({
                        name: item.name || item.description,
                        description: item.description || item.name,
                        quantity: item.quantity || 1,
                        unitPrice: item.unitPrice || (item.price / (item.quantity || 1)),
                        price: item.price,
                        priceWithTax: item.priceWithTax,
                        unit: item.unit
                    }))
                };
            });
            
        // Store the selected invoices data in the server's temporary storage
        try {
            const response = await fetch('/api/temp-storage', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(selectedInvoicesData)
            });
            
            if (!response.ok) {
                throw new Error('Failed to store invoice data');
            }
            
            const { id } = await response.json();
            console.log('Stored invoice data with ID:', id);
            
            // Navigate to the progress page with just the storage ID
            window.location.href = `/sync-invoices-progress?storageId=${id}`;
        } catch (error) {
            console.error('Error storing invoice data:', error);
            alert('Failed to prepare invoices for sync. Please try again.');
        }
    }

    // OLD REACTIVE STATEMENT - No longer needed
    // $: {
    //     if (jtInvoices && xeroInvoices) {
    //         computeInvoicesToSync();
    //     }
    // }

    function calculateTotal(items) {
        if (!Array.isArray(items)) return 0;
        return items.length;
    }
</script>

<Layout>
    <div class="container mx-auto px-4 py-8">
        <h1 class="text-2xl font-bold mb-6">Sync Historical Invoices</h1>

        <!-- Filter Section -->
        <div class="mb-6 p-4 bg-white rounded-lg shadow">
            <h2 class="text-lg font-semibold mb-4">Filter Invoices</h2>
            <div class="space-y-4">
                <p class="text-gray-600 mb-3">
                    Please select a time period or specific job to view invoices
                    {#if showPaid}
                        (including PAID) in Jobtread.
                    {:else}
                        PENDING in Jobtread.
                    {/if}
                </p>
                <div class="flex gap-4">
                    <select
                        class="flex-1 p-2 border rounded bg-white"
                        bind:value={selectedFilter}
                        on:change={handleFilterChange}
                    >
                        <option value="">Please select a filter...</option>
                        <option value="30">Last 30 days</option>
                        <option value="60">Last 60 days</option>
                        <option value="90">Last 90 days</option>
                        <option value="job">By Job (90 days)</option>
                        <option value="job-all">By Job (all time)</option>
                    </select>

                    {#if selectedFilter === 'job' || selectedFilter === 'job-all'}
                        <select
                            class="flex-1 p-2 border rounded bg-white {jobsFromInvoices.length === 0 ? 'border-orange-400' : ''}"
                            bind:value={selectedJob}
                            on:change={handleJobChange}
                            disabled={isLoading || jobsFromInvoices.length === 0}
                        >
                            {#if isLoading}
                                <option value="">Loading jobs...</option>
                            {:else if jobsFromInvoices.length === 0}
                                <option value="">No jobs with invoices found</option>
                            {:else}
                                <option value="">Select a job...</option>
                                {#each jobsFromInvoices as job}
                                    <option value={job.id}>{job.name} ({job.customer})</option>
                                {/each}
                            {/if}
                        </select>
                    {/if}
                </div>
            </div>
        </div>

        {#if showContent}
            {#if isLoading}
                <div class="flex justify-center items-center py-8">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
            {:else if error}
                <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                    {error}
                </div>
            {:else}

                <div class="bg-white rounded-lg shadow overflow-hidden">
                    <div class="p-4 border-b">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-6">
                                <div class="flex items-center">
                                    <input
                                        type="checkbox"
                                        class="form-checkbox h-5 w-5 text-primary"
                                        bind:checked={selectAll}
                                        on:change={handleSelectAll}
                                    >
                                    <span class="ml-2 text-gray-700">Select All</span>
                                </div>
                                
                                <!-- Toggle Switch for Hide Synced -->
                                <label class="inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        bind:checked={hideSynced}
                                        class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0"
                                    />
                                    <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    <span class="ms-3 text-sm font-medium text-gray-900">
                                        Hide Synced
                                        <span class="text-xs text-gray-500 font-normal">
                                            ({jtInvoices.filter(inv => inv.isSynced).length} synced, {jtInvoices.filter(inv => !inv.isSynced).length} need sync)
                                        </span>
                                    </span>
                                </label>

                                <!-- Toggle Switch for Show Paid -->
                                <label class="inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        bind:checked={showPaid}
                                        on:change={handleShowPaidChange}
                                        class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0"
                                    />
                                    <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    <span class="ms-3 text-sm font-medium text-gray-900">Include Paid</span>
                                </label>

                                <!-- Toggle Switch for Include Drafts -->
                                <label class="inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        bind:checked={showDrafts}
                                        on:change={handleShowDraftsChange}
                                        class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0"
                                    />
                                    <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    <span class="ms-3 text-sm font-medium text-gray-900">Include Drafts</span>
                                </label>
                            </div>
                            <div class="flex flex-wrap gap-2 items-center">
                                <button
                                    class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 bg-white text-gray-600 border border-gray-300 hover:bg-[#f0f4ff] disabled:opacity-40 disabled:cursor-not-allowed"
                                    disabled={!canMarkSelected}
                                    on:click={markSelectedAsSynced}
                                >
                                    Mark as Synced ({selectedInvoices.length})
                                </button>
                                <button
                                    class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                    disabled={selectedInvoices.length === 0}
                                    on:click={handleSyncClick}
                                >
                                    Sync Selected ({selectedInvoices.length})
                                </button>
                                <button
                                    class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                    on:click={handleSyncAllClick}
                                >
                                    Sync All
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Search Filter Section -->
                    <div class="p-4 pb-8 border-b bg-gray-50">
                        <div class="relative">
                            <div class="relative">
                                <!-- Magnifying glass icon -->
                                <svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search bills by number, vendor, or job (min. 2 chars)..."
                                    class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm {searchQuery && searchQuery.trim().length === 1 ? 'border-yellow-400' : ''}"
                                    value={searchQuery}
                                    on:input={handleSearch}
                                />
                                {#if searchQuery && searchQuery.trim().length === 1}
                                    <div class="absolute -bottom-6 left-0 text-xs text-yellow-600">
                                        Please enter at least 2 characters to search
                                    </div>
                                {/if}
                            </div>
                            {#if searchQuery}
                                <button
                                    class="absolute right-3 top-2 text-gray-400 hover:text-gray-600"
                                    on:click={() => { searchQuery = ""; }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                                    </svg>
                                </button>
                            {/if}
                        </div>
                    </div>

                    <!-- Results count -->
                    <div class="px-4 py-2 text-sm text-gray-600 border-b">
                        Showing {displayedInvoices.length} of {invoicesToSync.length} results
                    </div>

                    {#if displayedInvoices.length === 0}
                        <div class="p-8 text-center text-gray-500">
                            <p class="text-lg">
                                {#if searchQuery && searchQuery.trim().length >= 2}
                                    No invoices match your search for "{searchQuery}"
                                {:else}
                                    No invoices approved in JT and not Synced to Xero
                                {/if}
                            </p>
                        </div>
                    {:else}
                        <div class="overflow-x-auto">
                            <table class="min-w-full table-fixed max-w-full">
                                <thead class="bg-white">
                                    <tr>
                                        <th class="w-[5%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Select</th>
                                        <th class="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Invoice #</th>
                                        <th class="w-[15%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Customer</th>
                                        <th class="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Amount</th>
                                        <th class="w-[48%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Subject</th>
                                        <th class="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody class="bg-white divide-y divide-[#edf0f7]">
                                    {#each displayedInvoices as invoice}
                                    <tr class="hover:bg-[#f0f4ff] {invoice.isSynced ? 'bg-gray-50 opacity-75' : ''} {isDraft(invoice) ? 'opacity-60' : ''} {invoice.needsSync && !isDraft(invoice) ? 'cursor-pointer' : ''}" on:click={() => handleRowClick(invoice)}>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm" on:click={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                class="form-checkbox h-4 w-4 text-primary {invoice.needsSync && !isDraft(invoice) ? '' : 'opacity-50 cursor-not-allowed'}"
                                                checked={selectedInvoices.includes(invoice.id)}
                                                disabled={!invoice.needsSync || isDraft(invoice)}
                                                on:change={(event) => handleInvoiceSelect(invoice, event)}
                                            >
                                        </td>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm font-medium truncate">
                                            {invoice.jobNumber}-{invoice.number}
                                        </td>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{invoice.customer}</td>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm text-right">
                                            ${invoice.total ? invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                        </td>
                                        <td class="px-2 py-3 text-sm max-w-0">
                                            <div class="text-sm text-gray-900 truncate w-full" title={invoice.subject}>
                                                {invoice.subject || 'No subject'}
                                            </div>
                                            {#if invoice.lineItems && invoice.lineItems.length > 0}
                                                <div class="text-xs text-gray-500 mt-1 truncate w-full overflow-hidden h-4" title={invoice.lineItems.map(item => `${item.name} (${item.quantity}x)`).join(', ')}>
                                                    {invoice.lineItems.map(item => `${item.name} (${item.quantity}x)`).slice(0, 3).join(', ')}
                                                    {invoice.lineItems.length > 3 ? '...' : ''}
                                                </div>
                                            {/if}
                                        </td>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm">
                                            {#if invoice.needsSync}
                                                <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                    Needs Sync
                                                </span>
                                            {:else}
                                                <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    Synced
                                                </span>
                                            {/if}
                                            {#if isPaid(invoice)}
                                                <span class="ml-1 px-1.5 inline-flex text-xs leading-5 font-medium rounded-full bg-blue-100 text-blue-800">
                                                    Paid
                                                </span>
                                            {/if}
                                            {#if isDraft(invoice)}
                                                <span class="ml-1 px-1.5 inline-flex text-xs leading-5 font-medium rounded-full bg-gray-100 text-gray-600">
                                                    Draft
                                                </span>
                                            {/if}
                                        </td>
                                    </tr>
                                    {/each}
                                </tbody>
                            </table>
                        </div>
                    {/if}

                    {#if nextPage}
                        <div class="p-4 border-t">
                            <button
                                class="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                on:click={fetchMoreInvoices}
                                disabled={isLoadingMore}
                            >
                                {isLoadingMore ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    {/if}
                </div>
            {/if}
        {/if}
    </div>

    <!-- Mark as Synced Confirmation Modal -->
    {#if showMarkSyncedModal}
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" on:click={handleModalBackdropClick} on:keydown={(e) => e.key === 'Escape' && cancelMarkAsSynced()} role="button" tabindex="-1">
            <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" role="dialog" aria-modal="true">
                <h3 class="text-lg font-semibold text-gray-900 mb-3">Mark as Synced</h3>
                <p class="text-gray-600 mb-4">
                    Are you sure you want to mark <span class="font-semibold">{pendingMarkSyncedIds.length} invoice{pendingMarkSyncedIds.length > 1 ? 's' : ''}</span> as synced?
                </p>
                <p class="text-sm text-gray-500 mb-6">
                    This will prevent them from appearing as "needing sync" in the future.
                </p>
                <div class="flex gap-3 justify-end">
                    <button
                        on:click={cancelMarkAsSynced}
                        class="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        on:click={confirmMarkAsSynced}
                        class="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                    >
                        Mark as Synced
                    </button>
                </div>
            </div>
        </div>
    {/if}
</Layout> 